<script>
import { mapActions } from "vuex";
import { required, maxLength } from "@vuelidate/validators";
import { CREATE_SUPPORT_FEEDBACK } from "@/store/actions.type";
import useVuelidate from "@vuelidate/core";

export default {
  name: "Suggestion",
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      suggestion: {
        rating: null,
        positives: null,
        improvements: null,
      },
      isSuggestionUploading: false,
      fileSizeError: false,
    };
  },
  validations() {
    return {
      suggestion: {
        improvements: {
          required,
          maxLength: maxLength(500),
        },
        rating: {
          required,
        },
        positives: {
          maxLength: maxLength(500),
        },
      },
    };
  },
  methods: {
    ...mapActions({
      createSupportFeedback: CREATE_SUPPORT_FEEDBACK,
    }),
    goToLeaveFeedback() {
      this.$router.push("/feedback");
    },
    async submit() {
      this.v$.$touch();
      if (!this.v$.$invalid) {
        try {
          this.isSuggestionUploading = true;
          await this.createSupportFeedback(this.suggestion);
          // clear out all values in the suggestion
          Object.keys(this.suggestion).forEach(
            (key) => (this.suggestion[key] = null)
          );
          this.v$.$reset();
          this.isSuggestionUploading = false;
          // Remove file from uploader
          this.$refs.modal.show();
        } catch (error) {
          this.isSuggestionUploading = false;
          throw new Error(error);
        }
      }
    },
  },
};
</script>

<template>
  <cv-modal id="add-feedback" ref="modal">
    <template #title>Feedback sent</template>
    <template #content>
      <p>
        Thank you for providing us with feedback. We will use it to improve
        SkillsMatch.
      </p>
    </template>
    <template #primary-button>Close</template>
  </cv-modal>

  <cv-grid class="main">
    <div class="detail-tile-container">
      <div class="detail-description-container">
        <p class="detail-title"><strong>Leave a suggestion</strong></p>
        <p class="detail-description">
          Thank you for helping us to improve IBM SkillsMatch! Please leave a
          suggestions in a form below, so that we know where to improve. All
          responses are anonymous.
        </p>
      </div>
      <div class="detail-input-container">
        <cv-interactive-tooltip direction="bottom">
          <template #label>What we are doing well</template>
          <template #content>
            <p>Let us know what you like about SkillsMatch</p>
          </template>
        </cv-interactive-tooltip>
        <cv-text-area
          v-model="suggestion.positives"
          placeholder="e.g. I really like the design of the application."
        >
          <template
            v-if="
              !v$.suggestion.positives.maxLength &&
              v$.suggestion.positives.$error
            "
            #invalid-message
          >
            Feedback can't exceed 500 characters
          </template>
        </cv-text-area>
        <br />
        <span>
          Characters Remaining:
          {{ suggestion.positives ? 500 - suggestion.positives.length : 500 }}
        </span>
      </div>

      <div class="detail-input-container">
        <cv-interactive-tooltip direction="bottom">
          <template #label>Suggestions</template>
          <template #content>
            <p>
              Any information that you believe will help to improve SkillsMatch.
            </p>
          </template>
        </cv-interactive-tooltip>
        <cv-text-area v-model="suggestion.improvements">
          <template
            v-if="
              !v$.suggestion.improvements.required &&
              v$.suggestion.improvements.$error
            "
            #invalid-message
          >
            Please leave a suggestion
          </template>
          <template
            v-else-if="
              !v$.suggestion.improvements.maxLength &&
              v$.suggestion.improvements.$error
            "
            #invalid-message
          >
            The suggestion can't exceed 500 characters
          </template>
        </cv-text-area>
        <br />
        <span>
          Characters Remaining:
          {{
            suggestion.improvements ? 500 - suggestion.improvements.length : 500
          }}
        </span>
      </div>

      <div class="detail-input-container">
        <cv-interactive-tooltip direction="bottom">
          <template #label>Rating</template>
          <template #content>
            <p>On scale from 1 to 5, how would you rate SkillsMatch?</p>
          </template>
        </cv-interactive-tooltip>
        <!-- Change values to integer -->
        <cv-radio-group class="rating-container" legend-text=" ">
          <cv-radio-button
            v-model="suggestion.rating"
            name="group-1"
            label="1"
            value="1"
          />
          <cv-radio-button
            v-model="suggestion.rating"
            name="group-1"
            label="2"
            value="2"
          />
          <cv-radio-button
            v-model="suggestion.rating"
            name="group-1"
            label="3"
            value="3"
          />
          <cv-radio-button
            v-model="suggestion.rating"
            name="group-1"
            label="4"
            value="4"
          />
          <cv-radio-button
            v-model="suggestion.rating"
            name="group-1"
            label="5"
            value="5"
          />
        </cv-radio-group>
        <div v-if="v$.suggestion.rating.$error" class="error-message">
          A rating must be selected
        </div>
      </div>
    </div>
    <cv-button-set class="button-set">
      <cv-button kind="secondary" @click="goToLeaveFeedback"> Back </cv-button>
      <cv-button kind="primary" @click="submit"> Submit </cv-button>
    </cv-button-set>
  </cv-grid>
</template>

<style lang="scss" scoped>
.submit-button-container {
  display: flex;
  width: 100%;
}
.main {
  margin-left: 44px;
}
.rating-container {
  padding-top: 0.5rem;
}
.error-message {
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.34;
  letter-spacing: 0.32px;
  margin: 0.25rem 0 0;
  overflow: hidden;
  color: var(--cds-danger-02);
}
span {
  float: right;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.34;
  letter-spacing: 0.32px;
  display: inline-flex;
  align-items: center;
  color: var(--cds-text-02);
}

.button-set {
  max-width: 1103px;
  display: flex;
  justify-content: flex-end;
  gap: 2rem;
  margin-left: auto;
  margin-right: 15px;
  padding: 0 1rem;
}

@media (max-width: 47.75rem) {
  .button-set {
    display: block;
    margin: 0 auto;
    padding: 0 1rem;
  }

  .button-set > * {
    width: 33%;
    max-width: none;
  }
}
</style>
